/**
 * Pure helpers for the Contact Us form (COM-735). No I/O — the client file and
 * the server module both build on these.
 */
import {
  CONTACT_US_EMAIL_PATTERN,
  CONTACT_US_ERRORS,
} from "./contact-us.schemas";
import type {
  ContactUsFailureReason,
  ContactUsResult,
  ContactUsStatus,
  ContactUsValues,
} from "./types";

/**
 * The ONE live check the form runs before Submit (AC: "only the Email field has
 * field-level validation… format checking as the user types"). Format only —
 * an empty email is a Submit-time "required" failure, like every other field,
 * so clearing the box never flashes an error mid-edit.
 */
export function liveEmailError(value: string): string | null {
  if (value === "") return null;
  return CONTACT_US_EMAIL_PATTERN.test(value) ? null : CONTACT_US_ERRORS.email;
}

/** The SFRA controller the Worker bridges to (`org_hec/cartridge/controllers/ContactUs.js`). */
export const CONTACT_US_CONTROLLER = "ContactUs-Subscribe";

/** Same-origin resource route the browser posts to. */
export const CONTACT_US_ENDPOINT = "/resources/contact-us";

/** Success message stays up this long — the live form's 5s (`contactUs.js:135-138`). */
export const CONTACT_US_SUCCESS_VISIBLE_MS = 5000;

/**
 * `ContactUs-Subscribe` reads `req.form.{name,email,orderNo,options,description}`
 * (`ContactUs.js:14-22`) — SFRA's own names, not the form's, so the mapping
 * lives in exactly one place. The CSRF pair is added by `sfraBridge`.
 */
export function buildSfraContactUsBody(
  values: ContactUsValues,
): URLSearchParams {
  return new URLSearchParams({
    name: values.name,
    email: values.email,
    orderNo: values.orderNumber,
    options: values.subject,
    description: values.description,
  });
}

/** Form-encoded body for the browser → Worker POST. */
export function buildContactUsRequestBody(
  values: ContactUsValues,
): URLSearchParams {
  return new URLSearchParams({
    name: values.name,
    email: values.email,
    subject: values.subject,
    orderNumber: values.orderNumber,
    description: values.description,
  });
}

const FAILURE_REASONS: readonly ContactUsFailureReason[] = [
  "invalid",
  "rate_limited",
  "unavailable",
];

/**
 * Reads the action's reply. Anything that is not exactly the contract — an
 * HTML error page, a proxy's JSON, a truncated body — is `unavailable`, never
 * a success: the shopper must not be told a message was sent that wasn't.
 */
export function parseContactUsResult(json: unknown): ContactUsResult {
  if (typeof json === "object" && json !== null && "ok" in json) {
    if (json.ok === true) return { ok: true };
    const reason = "reason" in json ? json.reason : undefined;
    if (FAILURE_REASONS.includes(reason as ContactUsFailureReason)) {
      return { ok: false, reason: reason as ContactUsFailureReason };
    }
  }
  return { ok: false, reason: "unavailable" };
}

export function statusForResult(result: ContactUsResult): ContactUsStatus {
  return result.ok
    ? { kind: "succeeded" }
    : { kind: "failed", reason: result.reason };
}
